import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  FiSearch, FiFilter, FiGrid, FiList, FiBookmark, FiShare2, 
  FiHeart, FiEye, FiClock, FiChevronLeft, 
  FiChevronRight, FiColumns, FiX,
  FiMessageCircle, FiRefreshCw, FiTrash2
} from 'react-icons/fi';
import {
  faTriangleExclamation
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { connect } from "react-redux";
import "./enhanced-styles.css";
import "./modern-advanced-styles.css";

// Enhanced BlogList component with modern professional design
const EnhancedBlogList = (props) => {
  
  // Core State Management
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Advanced Filter and Search State
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTag, setSelectedTag] = useState("All");
  const [selectedAuthor, setSelectedAuthor] = useState("All");
  const [dateRange, setDateRange] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);
  
  // View and Pagination State
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list', or 'masonry'
  const [currentPage, setCurrentPage] = useState(1);
  const [blogsPerPage] = useState(9);
  
  // User Interaction State
  const [likedBlogs, setLikedBlogs] = useState(new Set());
  const [bookmarkedBlogs, setBookmarkedBlogs] = useState(new Set());

  // Get user info
  const { user } = props.user || {};

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.user?.email === process.env.REACT_APP_ADMIN || 
           user?.user?.email === process.env.REACT_APP_ARI_ADMIN;
  }, [user?.user?.email]);

  // Utility functions for enhanced features
  const calculateReadTime = (content) => {
    const wordsPerMinute = 200;
    const wordCount = content.split(' ').length;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const generateExcerpt = (content, maxLength = 150) => {
    const plainText = content.replace(/<[^>]*>/g, '');
    return plainText.length > maxLength 
      ? plainText.substring(0, maxLength) + '...'
      : plainText;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Aromatherapy': '#904d71',
      'DIY Recipes': '#c76b8e',
      'Wellness': '#e8a5c4',
      'Essential Oils': '#7b4397',
      'Beauty': '#dc2430'
    };
    return colors[category] || '#904d71';
  };

  const getBlogBadge = (blog) => {
    const daysSinceCreated = moment().diff(moment(blog.createDate), 'days');
    if (daysSinceCreated <= 3) return { type: 'new', label: 'New' };
    if (blog.views > 1000) return { type: 'trending', label: 'Trending' };
    if (blog.featured) return { type: 'featured', label: 'Featured' };
    return null;
  };

  // Enhanced data fetching
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [blogsResponse, categoriesResponse, tagsResponse] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_API_BASE}/blog/get`),
        axios.get(`${process.env.REACT_APP_API_BASE}/categories-blog/get`),
        axios.get(`${process.env.REACT_APP_API_BASE}/tag/get`)
      ]);
      
      if (blogsResponse.status === 'fulfilled') {
        const blogsData = Array.isArray(blogsResponse.value.data) 
          ? blogsResponse.value.data 
          : blogsResponse.value.data?.data || [];
        
        const enhancedBlogs = blogsData.map(blog => ({
          ...blog,
          readTime: calculateReadTime(blog.content || blog.text || ''),
          excerpt: generateExcerpt(blog.content || blog.text || '', 150),
          formattedDate: moment(blog.createDate).format("MMMM Do YYYY"),
          relativeDate: moment(blog.createDate).fromNow(),
          isNew: moment().diff(moment(blog.createDate), 'days') <= 7,
          categoryColor: getCategoryColor(blog.category),
          badge: getBlogBadge(blog),
          engagement: (blog.likes || 0) + (blog.comments || 0) + (blog.views || 0)
        }));

        setBlogs(enhancedBlogs);
        
        // Extract unique authors
        const uniqueAuthors = [...new Set(enhancedBlogs.map(blog => blog.author))];
        setAuthors(uniqueAuthors);
      }

      if (categoriesResponse.status === 'fulfilled') {
        const categoriesData = categoriesResponse.value.data?.data || [];
        setCategories(categoriesData);
      }

      if (tagsResponse.status === 'fulfilled') {
        // Tags data available for future enhancement
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load blogs. Please try again.');
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Enhanced filtering and searching
  const processedBlogs = useMemo(() => {
    let filtered = [...blogs];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(blog =>
        blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        blog.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory && selectedCategory !== 'All') {
      filtered = filtered.filter(blog => blog.category === selectedCategory);
    }

    // Tag filter
    if (selectedTag && selectedTag !== 'All') {
      filtered = filtered.filter(blog => 
        blog.tags && blog.tags.includes(selectedTag)
      );
    }

    // Author filter
    if (selectedAuthor && selectedAuthor !== 'All') {
      filtered = filtered.filter(blog => blog.author === selectedAuthor);
    }

    // Date range filter
    if (dateRange && dateRange !== 'All') {
      const now = moment();
      filtered = filtered.filter(blog => {
        const blogDate = moment(blog.createDate);
        switch (dateRange) {
          case 'Today':
            return blogDate.isSame(now, 'day');
          case 'This Week':
            return blogDate.isSame(now, 'week');
          case 'This Month':
            return blogDate.isSame(now, 'month');
          case 'This Year':
            return blogDate.isSame(now, 'year');
          default:
            return true;
        }
      });
    }

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return moment(b.createDate).diff(moment(a.createDate));
        case 'oldest':
          return moment(a.createDate).diff(moment(b.createDate));
        case 'popular':
          return b.engagement - a.engagement;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'author':
          return a.author.localeCompare(b.author);
        default:
          return 0;
      }
    });

    return filtered;
  }, [blogs, searchTerm, selectedCategory, selectedTag, selectedAuthor, dateRange, sortBy]);

  // Pagination
  const paginatedBlogs = useMemo(() => {
    const startIndex = (currentPage - 1) * blogsPerPage;
    return processedBlogs.slice(startIndex, startIndex + blogsPerPage);
  }, [processedBlogs, currentPage, blogsPerPage]);

  const totalPages = Math.ceil(processedBlogs.length / blogsPerPage);

  // User interaction handlers
  const handleLike = useCallback((blogId) => {
    setLikedBlogs(prev => {
      const newLiked = new Set(prev);
      if (newLiked.has(blogId)) {
        newLiked.delete(blogId);
      } else {
        newLiked.add(blogId);
      }
      return newLiked;
    });
    toast.success(likedBlogs.has(blogId) ? 'Removed from favorites' : 'Added to favorites');
  }, [likedBlogs]);

  const handleBookmark = useCallback((blogId) => {
    setBookmarkedBlogs(prev => {
      const newBookmarked = new Set(prev);
      if (newBookmarked.has(blogId)) {
        newBookmarked.delete(blogId);
      } else {
        newBookmarked.add(blogId);
      }
      return newBookmarked;
    });
    toast.success(bookmarkedBlogs.has(blogId) ? 'Removed bookmark' : 'Bookmarked');
  }, [bookmarkedBlogs]);

  const handleShare = useCallback((blog) => {
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: blog.excerpt,
        url: `${window.location.origin}/blog/${blog.id}`
      });
    } else {
      navigator.clipboard.writeText(`${window.location.origin}/blog/${blog.id}`);
      toast.success('Link copied to clipboard');
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success('Blogs refreshed');
  }, [fetchData]);

  // Admin function to delete blog
  const handleDeleteBlog = useCallback(async (blogId, blogTitle) => {
    if (!isAdmin()) {
      toast.error('Nu aveți permisiunea de a șterge articole');
      return;
    }

    const confirmMessage = `Sunteți sigur că doriți să ștergeți articolul "${blogTitle}"?\n\nAceastă acțiune nu poate fi anulată.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        const response = await axios.delete(`${process.env.REACT_APP_API_BASE}/blog/delete/${blogId}`);
        
        if (response.data.success || response.status === 200) {
          // Remove the blog from the state
          setBlogs(prevBlogs => prevBlogs.filter(blog => blog.id !== blogId));
          toast.success('Articolul a fost șters cu succes');
        } else {
          throw new Error('Ștergerea a eșuat');
        }
      } catch (error) {
        console.error('Error deleting blog:', error);
        toast.error('Eroare la ștergerea articolului: ' + error.message);
      }
    }
  }, [isAdmin]);

  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedCategory("All");
    setSelectedTag("All");
    setSelectedAuthor("All");
    setDateRange("All");
    setSortBy("newest");
    setCurrentPage(1);
  }, []);

  // Effects
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [processedBlogs]);

  // Component render
  if (loading) {
    return (
      <div className="enhanced-blog-container">
        <div className="loading-container" style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '60vh',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <FiRefreshCw className="spinning" style={{ fontSize: '3rem', color: '#904d71' }} />
          <p style={{ fontSize: '1.2rem', color: '#666' }}>Loading amazing content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="enhanced-blog-container">
        <div className="error-container" style={{ 
          textAlign: 'center', 
          padding: '60px 20px',
          color: '#666'
        }}>
          <FontAwesomeIcon icon={faTriangleExclamation} style={{ fontSize: '3rem', marginBottom: '20px', color: '#e74c3c' }} />
          <h3>Oops! Something went wrong</h3>
          <p>{error}</p>
          <button 
            onClick={handleRefresh}
            style={{
              background: 'linear-gradient(45deg, #904d71, #c76b8e)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '25px',
              cursor: 'pointer',
              fontSize: '1rem',
              marginTop: '20px'
            }}
          >
            <FiRefreshCw style={{ marginRight: '8px' }} />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="enhanced-blog-container">
      {/* Hero Section */}
      <div className="blog-hero-section">
        <div className="blog-hero-content">
          <h1 className="blog-hero-title">Discover Amazing Content</h1>
          <p className="blog-hero-subtitle">
            Explore our collection of insightful articles, tutorials, and stories 
            crafted to inspire and educate. Join thousands of readers on a journey of discovery.
          </p>
          <div className="blog-hero-stats">
            <div className="hero-stat-item">
              <span className="hero-stat-number">{blogs.length}</span>
              <span className="hero-stat-label">Articles</span>
            </div>
            <div className="hero-stat-item">
              <span className="hero-stat-number">{categories.length}</span>
              <span className="hero-stat-label">Categories</span>
            </div>
            <div className="hero-stat-item">
              <span className="hero-stat-number">{authors.length}</span>
              <span className="hero-stat-label">Authors</span>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Search Section */}
      <div className="container">
        <div className="advanced-search-section">
          <div className="search-header">
            <h2 className="search-title">
              <FiSearch />
              Search & Discover
            </h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                className="filters-toggle"
                onClick={() => setShowFilters(!showFilters)}
              >
                <FiFilter />
                Advanced Filters
              </button>
              <button 
                onClick={handleRefresh}
                disabled={refreshing}
                style={{
                  background: refreshing ? '#ccc' : 'linear-gradient(45deg, #904d71, #c76b8e)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '15px',
                  padding: '10px 15px',
                  cursor: refreshing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                <FiRefreshCw className={refreshing ? 'spinning' : ''} />
              </button>
            </div>
          </div>

          <div className="mega-search-box">
            <FiSearch className="mega-search-icon" />
            <input
              type="text"
              className="mega-search-input"
              placeholder="Search articles, authors, categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                className="search-clear-btn"
                onClick={() => setSearchTerm('')}
              >
                <FiX />
              </button>
            )}
          </div>

          <div className={`advanced-filters-grid ${showFilters ? 'expanded' : ''}`}>
            <div className="filter-group">
              <label className="filter-label">Category</label>
              <select
                className="filter-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="All">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Author</label>
              <select
                className="filter-select"
                value={selectedAuthor}
                onChange={(e) => setSelectedAuthor(e.target.value)}
              >
                <option value="All">All Authors</option>
                {authors.map(author => (
                  <option key={author} value={author}>
                    {author}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Date Range</label>
              <select
                className="filter-select"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
              >
                <option value="All">All Time</option>
                <option value="Today">Today</option>
                <option value="This Week">This Week</option>
                <option value="This Month">This Month</option>
                <option value="This Year">This Year</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="filter-label">Sort By</label>
              <select
                className="filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="popular">Most Popular</option>
                <option value="title">Title A-Z</option>
                <option value="author">Author A-Z</option>
              </select>
            </div>
          </div>

          {(searchTerm || selectedCategory !== 'All' || selectedAuthor !== 'All' || dateRange !== 'All') && (
            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <button
                onClick={clearFilters}
                style={{
                  background: 'none',
                  border: '2px solid #904d71',
                  color: '#904d71',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#904d71';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'none';
                  e.target.style.color = '#904d71';
                }}
              >
                <FiX style={{ marginRight: '5px' }} />
                Clear All Filters
              </button>
            </div>
          )}
        </div>

        {/* View Controls */}
        <div className="view-controls">
          <div className="view-mode-buttons">
            <button
              className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <FiGrid />
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <FiList />
            </button>
            <button
              className={`view-mode-btn ${viewMode === 'masonry' ? 'active' : ''}`}
              onClick={() => setViewMode('masonry')}
              title="Masonry View"
            >
              <FiColumns />
            </button>
          </div>

          <div className="results-info">
            <span className="results-count">
              {processedBlogs.length} {processedBlogs.length === 1 ? 'article' : 'articles'}
            </span>
            {searchTerm && (
              <span>
                for "{searchTerm}"
              </span>
            )}
          </div>
        </div>

        {/* Blog Grid */}
        {paginatedBlogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
            <FiSearch style={{ fontSize: '3rem', marginBottom: '20px', opacity: 0.5 }} />
            <h3>No articles found</h3>
            <p>Try adjusting your search criteria or filters</p>
          </div>
        ) : (
          <div className={`blog-grid ${viewMode}-view`}>
            {paginatedBlogs.map((blog, index) => (
              <article key={blog.id} className="enhanced-blog-card">
                <div className="blog-card-header">
                  <img
                    src={blog.image || '/api/placeholder/400/250'}
                    alt={blog.title}
                    className="blog-card-image"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/400/250';
                    }}
                  />
                  <div className="blog-card-overlay"></div>
                  
                  {blog.badge && (
                    <div className="blog-card-badges">
                      <span className={`blog-badge ${blog.badge.type}`}>
                        {blog.badge.label}
                      </span>
                    </div>
                  )}

                  <div className="blog-card-actions">
                    {/* Admin Delete Button - First for better visibility */}
                    {isAdmin() && (
                      <button
                        className="blog-action-btn admin-delete-btn"
                        onClick={() => handleDeleteBlog(blog.id, blog.title)}
                        title="Șterge articol"
                      >
                        <FiTrash2 />
                      </button>
                    )}
                    
                    <button
                      className={`blog-action-btn ${likedBlogs.has(blog.id) ? 'active' : ''}`}
                      onClick={() => handleLike(blog.id)}
                      title="Like"
                    >
                      <FiHeart />
                    </button>
                    <button
                      className={`blog-action-btn ${bookmarkedBlogs.has(blog.id) ? 'active' : ''}`}
                      onClick={() => handleBookmark(blog.id)}
                      title="Bookmark"
                    >
                      <FiBookmark />
                    </button>
                    <button
                      className="blog-action-btn"
                      onClick={() => handleShare(blog)}
                      title="Share"
                    >
                      <FiShare2 />
                    </button>
                  </div>
                </div>

                <div className="blog-card-content">
                  <div className="blog-card-meta">
                    <span 
                      className="blog-category-tag"
                      style={{ backgroundColor: blog.categoryColor }}
                    >
                      {blog.category}
                    </span>
                    <div className="blog-read-time">
                      <FiClock />
                      {blog.readTime} min read
                    </div>
                  </div>

                  <Link to={`/blog/${blog.id}`}>
                    <h3 className="blog-card-title">{blog.title}</h3>
                  </Link>

                  <p className="blog-card-excerpt">{blog.excerpt}</p>

                  <div className="blog-card-footer">
                    <div className="blog-author-info">
                      <div className="blog-author-avatar">
                        {blog.author.charAt(0).toUpperCase()}
                      </div>
                      <div className="blog-author-details">
                        <span className="blog-author-name">{blog.author}</span>
                        <span className="blog-publish-date">{blog.relativeDate}</span>
                      </div>
                    </div>

                    <div className="blog-engagement-stats">
                      <div className="engagement-stat">
                        <FiEye />
                        <span>{blog.views || 0}</span>
                      </div>
                      <div className="engagement-stat">
                        <FiHeart />
                        <span>{blog.likes || 0}</span>
                      </div>
                      <div className="engagement-stat">
                        <FiMessageCircle />
                        <span>{blog.comments || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Advanced Pagination */}
        {totalPages > 1 && (
          <div className="advanced-pagination">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
            >
              <FiChevronLeft />
              <FiChevronLeft />
            </button>
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
            >
              <FiChevronLeft />
              Previous
            </button>

            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              const pageNumber = Math.max(1, currentPage - 2) + index;
              if (pageNumber <= totalPages) {
                return (
                  <button
                    key={pageNumber}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNumber)}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
              <FiChevronRight />
            </button>
            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(totalPages)}
            >
              <FiChevronRight />
              <FiChevronRight />
            </button>

            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(EnhancedBlogList);
